import { useEffect, useState, useRef, useCallback } from 'react';
import { Github, Loader2, AlertCircle, ExternalLink, Clock, RefreshCw } from 'lucide-react';
import { buildApiUrl } from '../lib/apiBase';

// Timeout before showing "stuck" guidance (3 minutes)
const STUCK_TIMEOUT_MS = 3 * 60 * 1000;

interface GuestConnectGitHubProps {
  onConnected?: () => void;
}

export function GuestConnectGitHub({ onConnected }: GuestConnectGitHubProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [installUrl, setInstallUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isStuck, setIsStuck] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const successReceivedRef = useRef(false);

  const loadInstallUrl = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(buildApiUrl('/api/v1/guest/github/install-url'), {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch install URL');
      }
      const data = await response.json();
      setInstallUrl(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to prepare GitHub connection');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadInstallUrl();
  }, []);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // Listen for success message from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'github-install-success') {
        successReceivedRef.current = true;
        // Clear the stuck timeout since we got a success
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsStuck(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const cleanupAndReset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    popupRef.current = null;
    successReceivedRef.current = false;
    setIsConnecting(false);
    setIsStuck(false);
  }, []);

  const handleConnect = () => {
    if (!installUrl) return;

    // Reset state
    cleanupAndReset();
    setIsConnecting(true);
    successReceivedRef.current = false;

    const returnTo = `${window.location.pathname}${window.location.search}`;
    // Use GitHub state parameter for secure callback routing (no localStorage needed)
    // Only encode the returnTo part, not the "guest:" prefix, so we can detect it on callback
    const stateParam = `guest:${encodeURIComponent(returnTo)}`;
    // Append state with & or ? depending on whether URL already has query params
    // Note: Don't encode stateParam again - it will be URL-encoded by the browser when navigating
    const separator = installUrl.includes('?') ? '&' : '?';
    const urlWithState = `${installUrl}${separator}state=${stateParam}`;

    const width = 800;
    const height = 800;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      urlWithState,
      'github-install',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );
    popupRef.current = popup;

    // Start stuck timeout - if no success message received within timeout, show guidance
    timeoutRef.current = setTimeout(() => {
      if (!successReceivedRef.current && popupRef.current && !popupRef.current.closed) {
        setIsStuck(true);
      }
    }, STUCK_TIMEOUT_MS);

    // Poll for popup close
    pollTimerRef.current = setInterval(() => {
      if (popup?.closed) {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;

        if (successReceivedRef.current) {
          setIsConnecting(false);
          onConnected?.();
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          return;
        }

        let pollAttempts = 0;
        const maxAttempts = 10;
        const fallbackPoll = setInterval(async () => {
          pollAttempts += 1;

          try {
            const response = await fetch(buildApiUrl('/api/v1/guest/session'), { credentials: 'include' });
            if (response.ok) {
              const data = await response.json();
              if (data.hasGitHubConnection) {
                clearInterval(fallbackPoll);
                setIsConnecting(false);
                onConnected?.();
                return;
              }
            }
          } catch {
            // Ignore errors while polling
          }

          if (pollAttempts >= maxAttempts) {
            clearInterval(fallbackPoll);
            setIsConnecting(false);
          }
        }, 1000);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    }, 500);
  };

  const handleCancelStuck = () => {
    // Close popup if still open
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    cleanupAndReset();
  };

  if (isLoading) {
    return (
      <div className="border border-slate-700 rounded-lg p-6 bg-slate-800">
        <div className="flex items-center justify-center gap-2 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Preparing GitHub connection...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-900/50 rounded-lg p-6 bg-red-950/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium mb-1">Failed to prepare GitHub connection</p>
            <p className="text-red-300/70 text-sm">{error}</p>
          </div>
        </div>
        <button
          onClick={() => void loadInstallUrl()}
          className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Show stuck state with guidance when timeout reached
  if (isStuck && isConnecting) {
    return (
      <div className="border border-amber-900/50 rounded-lg bg-amber-950/20">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <Clock className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-amber-400">GitHub App Already Installed?</h3>
              <p className="text-sm text-amber-300/70 mt-1">
                It looks like the connection is taking longer than expected. This usually happens when
                the GitHub App is already installed on your account.
              </p>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-white mb-3">To reconnect, try one of these options:</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-amber-600/20 text-amber-400 flex items-center justify-center flex-shrink-0 text-xs font-medium mt-0.5">
                  1
                </div>
                <div className="text-sm">
                  <p className="text-slate-300">
                    <strong className="text-white">Modify app permissions:</strong> In the GitHub popup, 
                    change which repositories the app can access (add or remove one), then save. This 
                    triggers a new callback.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-amber-600/20 text-amber-400 flex items-center justify-center flex-shrink-0 text-xs font-medium mt-0.5">
                  2
                </div>
                <div className="text-sm">
                  <p className="text-slate-300">
                    <strong className="text-white">Clean reinstall:</strong> Uninstall the app from{' '}
                    <a
                      href="https://github.com/settings/installations"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-400 hover:text-amber-300 underline"
                    >
                      GitHub Settings → Applications
                    </a>
                    , then try connecting again.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCancelStuck}
              className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-md transition-colors flex items-center justify-center gap-2"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                handleCancelStuck();
                handleConnect();
              }}
              className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-700 rounded-lg bg-slate-800">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center">
            <Github className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Connect GitHub App</h3>
            <p className="text-sm text-slate-400">Install our GitHub App to access a single repository</p>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center flex-shrink-0 text-sm font-medium mt-0.5">
              1
            </div>
            <p className="text-slate-300 text-sm">Click the button below to open GitHub installation page</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center flex-shrink-0 text-sm font-medium mt-0.5">
              2
            </div>
            <p className="text-slate-300 text-sm">Select the organization or account to connect</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center flex-shrink-0 text-sm font-medium mt-0.5">
              3
            </div>
            <p className="text-slate-300 text-sm">Grant access to the repository you want to audit</p>
          </div>
        </div>

        <button
          onClick={handleConnect}
          disabled={isConnecting || !installUrl}
          className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-md transition-colors flex items-center justify-center gap-2"
        >
          {isConnecting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Waiting for GitHub...
            </>
          ) : (
            <>
              <Github className="w-5 h-5" />
              Install GitHub App
              <ExternalLink className="w-4 h-4" />
            </>
          )}
        </button>

        <p className="text-xs text-slate-500 mt-3 text-center">
          This will open a new window. Make sure pop-ups are allowed.
        </p>
      </div>
    </div>
  );
}
